import React, { useMemo } from 'react'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { BEIGE_500, BEIGE_600, GRAY_400, GRAY_50, GRAY_700, GRAY_900, GREEN_600, WHITE } from 'constants/styling/theme'
import type { ResolvedShellProduct, ResolvedVariant } from 'models/purchaseFlowRevamp'
import { hasProductGroupDetails, useProductGroupDetails } from '../../components/productInfo'
import { MUIBadge } from 'components/common/MUIBadge'
import { ProductGroupIcon } from './ProductGroupIcon.component'
import { ProductPreview } from './ProductPreview.component'
import { isQuotaFree } from '../../store/quotaPreview'
import { PfClickableCard } from '../../components/PfClickableCard.component'
import { ClientPrice } from '../../components/ClientPrice.component'
import { PF_CARD_HOVER_SX } from '../../components/cardHover'

interface Props {
  /** Resolved shell product. */
  product: ResolvedShellProduct
  /** Callback when the user clicks the card. */
  onSelect: (product: ResolvedShellProduct) => void
  /** Callback for the card's "more information" action. */
  onShowInfo: (product: ResolvedShellProduct) => void
  /**
   * Card shape. `CategorySection` picks it per section so a whole row is one
   * shape — a mixed row would leave the 180px column empty on some cards and
   * ragged on others.
   */
  layout?: 'thumb' | 'icon'
}

/**
 * Single product card on the catalogue grid, in one of the designer's two card
 * shapes (prototype `.pf-item` / `.pf-item--thumb`, Figma 5616:74982):
 *
 *   - `thumb` — a 180px square thumbnail on the LEFT, name / description / price
 *     stacked to its right. Two per row. What every visual product uses.
 *   - `icon` — no thumbnail: a 24px product glyph beside the name, then the
 *     description, then the footer, all stacked. Three per row. Documents use
 *     this because they have no artwork by design, not for want of an export.
 *
 * Both shapes carry the same content — preview/glyph, name, badges, description,
 * starting price ("from X"), and a "more information" action where there is
 * something to say. The card itself is the buy affordance: clicking it hands off
 * to the consumer, which opens the ProductDrawer.
 *
 * Country-awareness: if every variant is unpriced for the active country,
 * the card is rendered with a muted tint and a "Not available" helper
 * instead of a price. The card stays visible (per master-plan feedback:
 * this is discoverability, not a hard filter).
 */
/**
 * The card's "more information" button — an icon-only control the design system
 * has no variant for, so its shape is set here.
 *
 * A 28x28 SQUARE, sized rather than padded. At the button's own padding it came
 * out 34x30, and a full border-radius on a non-square box draws an ELLIPSE —
 * which is what made the hover read lopsided, a ring wider than it was tall
 * around a round glyph. `box-sizing` is border-box, so the hover border sits
 * inside the 28 and the 20px glyph keeps 3px of air, as the prototype ships it.
 *
 * Exported so both invariants can be pinned without a browser: the box must stay
 * square, and the ring must stay behind a fine-pointer query — on touch there is
 * no hover, and the rule would be dead weight the tap target has to pay for.
 */
export const INFO_BUTTON_SX = {
  width: '2.8rem',
  height: '2.8rem',
  padding: 0,
  flexShrink: 0,
  color: GRAY_700,
  // Reserved at rest and transparent, so only the colour moves on hover and the
  // button never changes size.
  border: '1px solid transparent',
  '@media (hover: hover) and (pointer: fine)': {
    // The card and the info button do different things, and hovering said nothing
    // about which. Both already had a hover state; they simply landed on the same
    // colour — the card takes BEIGE_500 and the button BEIGE_600, and the button
    // sits INSIDE the card, so by the time it paints the ground is already beige.
    // Contrast between the two is 1.025:1, where perceptible starts around 1.1.
    //
    // No fill can fix it: WHITE on the hovered card is 1.078:1 and the glyph is
    // already GRAY_700, so there is nothing left to darken. The signal has to be
    // an EDGE rather than a field — a hairline reads better than its ratio
    // suggests, being contour detection rather than surface discrimination.
    // GRAY_400 is the strongest neutral available here, at 1.262:1.
    '&:hover': { backgroundColor: WHITE, borderColor: GRAY_400 },
  },
  // Dropping the label shrank the hit area to 28x28, under the 44px the lightbox
  // controls already respect. It is padded back out on touch by growing the
  // TARGET, not the box — a 44px box would set the whole footer row's height for
  // a 20px price line.
  '@media (pointer: coarse)': {
    position: 'relative',
    // Grows INWARD — up and to the left — never outward. The button sits at the
    // card's bottom-right and the catalogue row scrolls horizontally, so overflow
    // past the card would give the carousel a scrollbar out of a tap target. 16px
    // each way still measures 44x44, onto the card's own empty space.
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '-1.6rem',
      left: '-1.6rem',
      right: 0,
      bottom: 0,
    },
  },
} as const

export const ProductCard: React.FC<Props> = ({ product, onSelect, onShowInfo, layout = 'thumb' }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  const pricedVariants = useMemo<ResolvedVariant[]>(
    () => product.variants.filter((v) => v.active && v.priced && v.price),
    [product.variants]
  )

  const isAvailable = pricedVariants.length > 0

  const details = useProductGroupDetails(product.productGroupCode)
  const hasDetails = hasProductGroupDetails(details)

  const lowestPrice = useMemo(() => {
    if (pricedVariants.length === 0) return null
    return pricedVariants.reduce((cheapest, current) => {
      if (!current.price) return cheapest
      if (!cheapest?.price) return current
      return Number(current.price.unitPrice) < Number(cheapest.price.unitPrice)
        ? current
        : cheapest
    }, pricedVariants[0])
  }, [pricedVariants])

  const badges = useMemo(() => {
    const list: string[] = []
    if (product.tagIds?.includes('popular')) list.push(t('purchase_flow_revamp:catalog.badge_popular'))
    if (product.tagIds?.includes('new')) list.push(t('purchase_flow_revamp:catalog.badge_new'))
    return list
  }, [product.tagIds, t])

  const handleSelect = () => onSelect(product)

  const isThumb = layout === 'thumb'

  return (
    <PfClickableCard
      onActivate={handleSelect}
      direction={isThumb ? 'row' : 'column'}
      alignItems={isThumb ? 'stretch' : undefined}
      // 24px between the thumbnail and the text it belongs to; the icon card has
      // no such split, its own header row owns the spacing.
      gap={isThumb ? 2.4 : 0}
      data-cy={`pf-revamp-product-card-${product.id}`}
      sx={{
        ...PF_CARD_HOVER_SX,
        opacity: isAvailable ? 1 : 0.7,
        '&:focus-visible': { backgroundColor: GRAY_50 },
      }}
    >

      {isThumb &&
        <ProductPreview
          preview={product.preview}
          alt={product.displayName}
          width="18rem"
          height="18rem"
          borderRadius="1rem"
          interactive
        />
      }

      <Stack flex="1 0 0" minWidth={0}>

        <Stack direction="row" alignItems="center" gap={0.8}>

          {!isThumb && <ProductGroupIcon productGroupCode={product.productGroupCode} />}

          <Typography
            variant={isThumb ? 'text-lg' : 'text-md'}
            fontWeight={550}
            color={GRAY_900}
            sx={{ letterSpacing: '-0.72px' }}
          >
            {product.displayName}
          </Typography>

        </Stack>

        {badges.length > 0 &&
          <Stack direction="row" gap={0.4} flexWrap="wrap" alignSelf="flex-start" marginTop={0.2}>
            {badges.map((badge) => (
              <MUIBadge key={badge} label={badge} color="gray" size="sm" />
            ))}
          </Stack>
        }

        {product.description &&
          <Typography
            variant="text-sm"
            fontWeight={350}
            color={GRAY_700}
            marginTop={0.8}
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              letterSpacing: '-0.56px',
            }}
          >
            {product.description}
          </Typography>
        }

        {/* Footer pinned to the bottom of the body, so price and CTA line up
            across a row of cards whose descriptions differ in length. */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          marginTop="auto"
          paddingTop={1.2}
        >

          {isAvailable && lowestPrice?.price &&
            <Typography variant="text-sm">

              {/* "From" only when there are prices to choose between; one product has one price (QA-518). */}
              {pricedVariants.length > 1 &&
                <Box component="span" color={GRAY_700}>
                  {t('purchase_flow_revamp:catalog.price_from')}{' '}
                </Box>
              }

              <Box component="span" display="inline-flex" color={isQuotaFree(lowestPrice) ? GREEN_600 : GRAY_900} fontWeight={550}>
                <ClientPrice unitPrice={lowestPrice.price.unitPrice} currency={lowestPrice.price.currency} variant="text-sm" />
              </Box>

            </Typography>
          }

          {!isAvailable &&
            <Typography variant="text-xs" color={GRAY_700}>
              {t('purchase_flow_revamp:catalog.not_available_in_country')}
            </Typography>
          }

          {hasDetails &&
          <IconButton
            aria-label={t('purchase_flow_revamp:product_info.more_information_about', { product: product.displayName })}
            title={t('purchase_flow_revamp:product_info.more_information')}
            onClick={(e) => {
              e.stopPropagation()
              onShowInfo(product)
            }}
            size="small"
            sx={INFO_BUTTON_SX}
          >
            <Box component="svg" viewBox="0 0 24 24" aria-hidden sx={{ display: 'block', width: '1.6rem', height: '1.6rem' }}>
              <g fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <line x1="12" y1="11" x2="12" y2="16" />
              </g>
              <circle cx="12" cy="8" r="0.6" fill="currentColor" stroke="currentColor" strokeWidth={1.75} />
            </Box>
          </IconButton>
          }

        </Stack>

      </Stack>

    </PfClickableCard>
  )
}
